/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Table, Thead, Tr, Th, Sortable } from "@lightspeed/cirrus-table";
import { Card } from "@lightspeed/flame/Card";
import Row from "./taxesTable/Row";
import Loader from "../Loader";
import { useSelector } from "react-redux";
import * as sortFns from "../../utils/sortFn";
import ClassDepLocInfo from "./ClassDepLocInfo";
import { normalizeQuickBooksAccountList, normalizeQuickBooksClassDepartmentList } from "../../utils/quickbooksClassDepartment";
import { getIntegrationName, isAcombaIntegration, isAcombaSdkIntegration, isD365Integration, normalizeClassDimensionList } from "../../utils/d365Dimensions";
import { fetchAcombaSdkLists } from "../../utils/acombaSdk";

const TaxesTable = ({ data, scrollPos, sortState, changeSortState }) => {
  const ui = useSelector((state) => state.ui);
  const database = useSelector((state) => state.database);

  const [sortedData, setSortedData] = useState([]);
  const [accountsList, setAccountsList] = useState([]);
  const [classList, setClassList] = useState({ classes: [], departments: [] });
  const [d365DimensionsList, setD365DimensionsList] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(true);

  const sortValues = ["unsorted", "desc", "asc"];
  const reducer = (acc, cur) => acc + cur;
  const firstRow = Array.isArray(data) ? data[0] : null;
  const currentCompanyId =
    Number(database.currentCompany?.id) > 0 ? database.currentCompany.id : null;
  const selectedCompanyId =
    currentCompanyId ??
    database.currentShop?.company_id ??
    firstRow?.company_id;
  const selectedCompanyMeta = [
    ...(database.retail || []),
    ...(database.restoK || []),
  ].find((company) => String(company?.company_id) === String(selectedCompanyId));
  const d365Integration = isD365Integration(
    database.currentCompany,
    database.currentShop,
    selectedCompanyMeta,
    firstRow
  );
  const quickBooksIntegration = getIntegrationName(
    database.currentCompany, database.currentShop, selectedCompanyMeta, firstRow
  ) === "QUICKBOOKS";
  const acombagoIntegration = isAcombaIntegration(
    database.currentCompany,
    database.currentShop,
    selectedCompanyMeta,
    firstRow
  );
  const acombaSdkIntegration = isAcombaSdkIntegration(
    database.currentCompany,
    database.currentShop,
    selectedCompanyMeta,
    firstRow
  );

  const quickBooksDropShopId =
    database.currentCompany?.type === "RESTOK"
      ? database.currentShop?.area_id ?? database.currentShop?.shop_id ?? 0
      : database.currentShop?.shop_id ?? 0;
  const isRetailR = database.currentCompany.type === "RETAIL";
  const accountingColsCount = (isRetailR ? 6 : 5) - (acombagoIntegration ? 1 : 0);

  const COL = {
    COMPANY: 0,
    TAX_ID: 1,
    TAX_NAME: 2,
    ACCOUNT: 3,
    ACCOUNT_DESC: 4,
    CDL: 5,
    CUSTOMER: 6,
    TAX_CLASS_ALLOCATION: 7,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (acombaSdkIntegration) {
          const { accounts, projects } = await fetchAcombaSdkLists({
            ip: database.currentShop?.ACOMBA_SDK_IP,
            port: database.currentShop?.ACOMBA_SDK_PORT,
          });
          setAccountsList(normalizeQuickBooksAccountList(accounts));
          setCustomersList(projects);
          setClassList({ classes: [], departments: [] });
          setD365DimensionsList([]);
          return;
        }

        const [accRes, classRes, customerRes] = await Promise.all([
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "QuickBooksIMPORT DROP_ACCOUNT_LIST",
            }),
          }),
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: quickBooksIntegration
                ? "QuickBooksIMPORT DROP_CLASSDEPARTMENT"
                : "QuickBooksIMPORT DROP_CLASS_LIST_CLASS",
            }),
          }),
          fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "QuickBooksIMPORT DROP_CUSTOMER_LIST",
            }),
          }),
        ]);

        const accountsData = await accRes.json();
        const classDataRaw = await classRes.json();
        const customerDataRaw = await customerRes.json();

        setAccountsList(normalizeQuickBooksAccountList(accountsData));
        setCustomersList(Array.isArray(customerDataRaw) ? customerDataRaw : []);

        const cleanedClassList = quickBooksIntegration
          ? normalizeQuickBooksClassDepartmentList(classDataRaw)
          : normalizeClassDimensionList(classDataRaw);

        setClassList(cleanedClassList);
        if (d365Integration) {
          const dimensionRes = await fetch("https://reportapi.beesql.com/api/admin/retailr/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: database.currentCompany.id,
              shop_id: quickBooksDropShopId,
              xoption: "D365DimensionsList",
            }),
          });
          const dimensionDataRaw = await dimensionRes.json();
          setD365DimensionsList(normalizeClassDimensionList(dimensionDataRaw));
        } else {
          setD365DimensionsList([]);
        }
      } catch (err) {
        console.error("Failed to fetch tax mapping data:", err);
        setAccountsList([]);
        setClassList({ classes: [], departments: [] });
        setCustomersList([]);
      } finally {
        setLoading(false);
      }
    };

    if (database?.currentCompany?.id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [
    database.currentCompany.id,
    quickBooksDropShopId,
    d365Integration,
    quickBooksIntegration,
  ]);

  useEffect(() => {
    if (Array.isArray(data)) {
      setSortedData([...data]);
    } else {
      console.error("TaxesTable: Expected data to be an array, but got:", data);
      setSortedData([]);
    }
  }, [data]);

  useEffect(() => {
    if (Array.isArray(data) && sortState.reduce(reducer) > 0) {
      let newSortedData = [...data];

      if (sortState[COL.COMPANY] === 1) {
        newSortedData.sort(sortFns.customSortGen("company_id"));
      } else if (sortState[COL.COMPANY] === 2) {
        newSortedData.sort(sortFns.customSortGen("company_id")).reverse();
      }

      if (sortState[COL.TAX_ID] === 1) {
        newSortedData.sort(sortFns.customSortGen("tax_id"));
      } else if (sortState[COL.TAX_ID] === 2) {
        newSortedData.sort(sortFns.customSortGen("tax_id")).reverse();
      }

      if (sortState[COL.TAX_NAME] === 1) {
        newSortedData.sort(sortFns.customSortGenString("tax_name"));
      } else if (sortState[COL.TAX_NAME] === 2) {
        newSortedData.sort(sortFns.customSortGenString("tax_name")).reverse();
      }

      if (sortState[COL.ACCOUNT] === 1) {
        newSortedData.sort(sortFns.customSortGenString("account_id"));
      } else if (sortState[COL.ACCOUNT] === 2) {
        newSortedData.sort(sortFns.customSortGenString("account_id")).reverse();
      }

      if (sortState[COL.ACCOUNT_DESC] === 1) {
        newSortedData.sort(sortFns.customSortGenString("account_description"));
      } else if (sortState[COL.ACCOUNT_DESC] === 2) {
        newSortedData
          .sort(sortFns.customSortGenString("account_description"))
          .reverse();
      }

      if (sortState[COL.CDL] === 1) {
        newSortedData.sort(
          sortFns.customSortGenString("class_department_location")
        );
      } else if (sortState[COL.CDL] === 2) {
        newSortedData
          .sort(sortFns.customSortGenString("class_department_location"))
          .reverse();
      }

      if (sortState[COL.CUSTOMER] === 1) {
        newSortedData.sort(sortFns.customSortGenString("qb_customer_id"));
      } else if (sortState[COL.CUSTOMER] === 2) {
        newSortedData
          .sort(sortFns.customSortGenString("qb_customer_id"))
          .reverse();
      }

      if (sortState[COL.TAX_CLASS_ALLOCATION] === 1) {
        newSortedData.sort(sortFns.customSortGenString("tax_class_allocation"));
      } else if (sortState[COL.TAX_CLASS_ALLOCATION] === 2) {
        newSortedData
          .sort(sortFns.customSortGenString("tax_class_allocation"))
          .reverse();
      }

      setSortedData(newSortedData);
    } else {
      setSortedData(Array.isArray(data) ? [...data] : []);
    }
  }, [sortState, data]);

  if (sortedData.length === 0 && ui.search) {
    return (
      <div className="loader-no-item">
        <h3>No items match your search</h3>
      </div>
    );
  }

  if (loading || !Array.isArray(sortedData) || sortedData.length === 0) {
    return <Loader />;
  }
  return (
    <Card top="true">
      <div className="payment-table-wrap">
        <Table>
          <Thead>
            <Tr className={`section-row ${ui.theme === "flame" ? "" : "dark-table-section"}`}>
              <Th colSpan="3" className="section-title section-title-pos">
                <div className="section-badge">POS</div>
              </Th>
              <Th colSpan={accountingColsCount} className="section-title section-title-acc">
                <div className="section-badge">Accounting</div>
              </Th>
            </Tr>

            {scrollPos === true ? (
              <Tr className={`${ui.theme === "flame" ? "" : "dark-table"}`}>
                <Sortable.Th
                  width={isRetailR ? "10%" : "12%"}
                  direction={sortValues[sortState[COL.COMPANY]]}
                  onClick={() => changeSortState(COL.COMPANY)}
                  className="th-pos"
                >
                  Company
                </Sortable.Th>

                <Sortable.Th
                  width={isRetailR ? "8%" : "10%"}
                  direction={sortValues[sortState[COL.TAX_ID]]}
                  onClick={() => changeSortState(COL.TAX_ID)}
                  className="th-pos"
                >
                  Tax ID
                </Sortable.Th>

                <Sortable.Th
                  width={isRetailR ? "14%" : "18%"}
                  direction={sortValues[sortState[COL.TAX_NAME]]}
                  onClick={() => changeSortState(COL.TAX_NAME)}
                  className="th-pos th-pos-last"
                >
                  Tax Name
                </Sortable.Th>

                <Sortable.Th
                  width={isRetailR ? "13%" : "15%"}
                  direction={sortValues[sortState[COL.ACCOUNT]]}
                  onClick={() => changeSortState(COL.ACCOUNT)}
                  className="th-acc th-acc-first"
                >
                  Account Id
                </Sortable.Th>

                <Sortable.Th
                  width={isRetailR ? "17%" : "20%"}
                  direction={sortValues[sortState[COL.ACCOUNT_DESC]]}
                  onClick={() => changeSortState(COL.ACCOUNT_DESC)}
                  className="th-acc"
                >
                  Account Description
                </Sortable.Th>

                {!acombagoIntegration && (


                  <Sortable.Th
                  width={isRetailR ? "9%" : "10%"}
                  direction={sortValues[sortState[COL.CDL]]}
                  onClick={() => changeSortState(COL.CDL)}
                  className="th-acc"
                >
                  Class/Dep/Loc <ClassDepLocInfo />
                </Sortable.Th>


                )}
                <Sortable.Th
                  width={isRetailR ? "9%" : "10%"}
                  direction={sortValues[sortState[COL.CUSTOMER]]}
                  onClick={() => changeSortState(COL.CUSTOMER)}
                  className="th-acc"
                >
                  {acombagoIntegration ? "Project" : "Customer"}
                </Sortable.Th>

                {isRetailR && (
                  <Sortable.Th
                    width="15%"
                    direction={sortValues[sortState[COL.TAX_CLASS_ALLOCATION]]}
                    onClick={() => changeSortState(COL.TAX_CLASS_ALLOCATION)}
                    className="th-acc"
                  >
                    Tax Class Allocation
                  </Sortable.Th>
                )}

                <Th width="5%" textAlign="right" className="th-acc th-plain th-action">
                  Action
                </Th>
              </Tr>
            ) : (
              <Tr className={`${ui.theme === "flame" ? "" : "dark-table-fake"}`}>
                <Th className="table-th-sticky th-pos" width={isRetailR ? "10%" : "12%"}>
                  Company
                </Th>
                <Th className="table-th-sticky th-pos" width={isRetailR ? "8%" : "10%"}>
                  Tax ID
                </Th>
                <Th className="table-th-sticky th-pos th-pos-last" width={isRetailR ? "14%" : "18%"}>
                  Tax Name
                </Th>
                <Th className="table-th-sticky th-acc th-acc-first" width={isRetailR ? "13%" : "15%"}>
                  Account Id
                </Th>
                <Th className="table-th-sticky th-acc" width={isRetailR ? "17%" : "20%"}>
                  Account Description
                </Th>
                {!acombagoIntegration && (

                  <Th className="table-th-sticky th-acc" width={isRetailR ? "9%" : "10%"}>
                  Class/Dep/Loc <ClassDepLocInfo />
                </Th>

                )}
                <Th className="table-th-sticky th-acc" width={isRetailR ? "9%" : "10%"}>
                  {acombagoIntegration ? "Project" : "Customer"}
                </Th>
                {isRetailR && (
                  <Th className="table-th-sticky th-acc" width="15%">
                    Tax Class Allocation
                  </Th>
                )}
                <Th
                  className="table-th-sticky th-acc th-plain th-action"
                  width="5%"
                  textAlign="right"
                >
                  Action
                </Th>
              </Tr>
            )}
          </Thead>

          <tbody className="t-body">
            {sortedData.map((item, index) => (
              <Row
                key={item.tax_id}
                item={item}
                accountsList={accountsList}
                classList={classList}
                d365DimensionsList={d365DimensionsList}
                customersList={customersList}
                isD365Integration={d365Integration || isD365Integration(item)}
                hideClassDepartmentColumn={acombagoIntegration || isAcombaIntegration(item)}
                rowIndex={index}
              />
            ))}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

export default TaxesTable;
